import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { Banner, Button, Card, DataTable, Empty, Field, Input, Pill, Spinner, Td } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useSession } from '../lib/session';
import { useAsync } from '../lib/useAsync';

/**
 * Sale files. The listing originates here: a brokerage agent adds a folio and
 * pings the occupying owner. Watching the list is not a door. Roster membership
 * admits nobody. Title does not list.
 */

interface BrokerageFile {
  parcelId: string;
  county: string;
  folio: string;
  address: string;
  agentEmail: string | null;
  agentCompany: string | null;
  status: 'admitted' | 'pending' | 'pending_owner' | 'closed';
  invitedAt: string | null;
  admittedAt: string | null;
}

export default function Files() {
  const { contractor } = useSession();
  const files = useAsync(() => api.get<{ files: BrokerageFile[] }>('/api/brokerage/files'), []);
  const [form, setForm] = useState({ county: '', folio: '', address: '', ownerEmail: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (contractor?.companyKind !== 'brokerage') return <Navigate to="/" replace />;
  const agent = contractor.permissionLevel === 'field';
  const canList = contractor.permissionLevel !== 'read_only';

  const pingOwner = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/brokerage/listings', {
        county: form.county.trim(),
        folio: form.folio.trim(),
        address: form.address.trim(),
        ownerEmail: form.ownerEmail.trim(),
      });
      setForm({ county: '', folio: '', address: '', ownerEmail: '' });
      await files.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not ping the owner');
    } finally {
      setBusy(false);
    }
  };

  const closeSale = async (parcelId: string) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/parcels/${parcelId}/listings/close`, {});
      await files.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not close this sale');
    } finally {
      setBusy(false);
    }
  };

  return <div className="space-y-6">
    <div>
      <h1 className="text-xl font-bold">Sale files</h1>
      <p className="mt-1 text-sm text-muted">
        {agent
          ? 'Add a folio from your listing. That pings the occupying owner — they tap. Being on the office roster alone attaches you to nothing.'
          : 'Sale files for this office. Listing agents add the house; the owner activates. Title does not list.'}
      </p>
    </div>
    {canList && (
      <Card title="Add a listing">
        <form className="space-y-4" onSubmit={(e) => void pingOwner(e)}>
          {error && <Banner tone="urgent">{error}</Banner>}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="County">
              <Input required value={form.county} onChange={(e) => setForm({ ...form, county: e.target.value })} />
            </Field>
            <Field label="Folio">
              <Input required value={form.folio} onChange={(e) => setForm({ ...form, folio: e.target.value })} />
            </Field>
          </div>
          <Field label="Address">
            <Input required minLength={5} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
          </Field>
          <Field label="Owner email" hint="Jane’s login. This is the ping — not occupying, not certified mail.">
            <Input required type="email" value={form.ownerEmail} onChange={(e) => setForm({ ...form, ownerEmail: e.target.value })} />
          </Field>
          <Button disabled={busy} type="submit">{busy ? 'Pinging…' : 'Ping the owner'}</Button>
        </form>
      </Card>
    )}
    {files.loading ? <Spinner label="Loading your sale files…" /> :
      files.error ? <Banner title="Could not load sale files">{files.error} <Button variant="secondary" onClick={() => void files.reload()}>Reload files</Button></Banner> :
      !files.data?.files.length ? <Card><Empty>{agent
        ? 'No sale files yet. Add a folio above to ping the occupying owner.'
        : 'No sale files yet. Files appear when an agent of this office lists a folio.'}</Empty></Card> :
      <Card>
        <DataTable head={['Folio / address', 'Listing person', 'Access']} empty="No sale files recorded.">
          {files.data.files.map((f) => <tr key={f.parcelId} className="border-b border-border">
            <Td><strong>{f.county} · {f.folio}</strong><p className="text-xs text-muted">{f.address}</p></Td>
            <Td>{f.agentEmail ?? f.agentCompany ?? '—'}{f.agentCompany && f.agentEmail ? <p className="text-xs text-muted">{f.agentCompany}</p> : null}</Td>
            <Td>
              <div className="flex items-center gap-2">
                <Pill tone={f.status === 'admitted' ? 'ok' : f.status === 'closed' ? 'none' : 'watch'}>
                  {f.status === 'admitted' ? 'Admitted' : f.status === 'closed' ? 'Closed' : 'Invited · admission pending'}
                </Pill>
                {canList && f.status === 'admitted' && (
                  <Button variant="secondary" disabled={busy} onClick={() => void closeSale(f.parcelId)}>Close sale</Button>
                )}
              </div>
            </Td>
          </tr>)}
        </DataTable>
        <p className="mt-3 text-xs text-muted">This list is a record, never a door: it carries no seat links and grants no parcel access.</p>
      </Card>}
  </div>;
}
